/* eslint-disable import/no-extraneous-dependencies */
import { MoreOutlined } from "@ant-design/icons";
import CryptoJS from "crypto-js";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";
import { saveAs } from "file-saver";
import i18next from "i18next";
import { ActionDropdown, AntDropdown, Popovers } from "../components";
import config from "../config";
import modalNotification from "./notification";
import logger from "./logger";

let localeData = require("dayjs/plugin/localeData");
let utc = require("dayjs/plugin/utc");
let timezone = require("dayjs/plugin/timezone");

dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

export const defaultCountryCode = "+44";

/**
 * Constructs an item object with the provided parameters.
 * @param {string} path - The path of the item.
 * @param {string} label - The label of the item.
 * @param {string} key - The key of the item.
 * @param {string} icon - The icon of the item.
 * @param {Array} children - The children of the item.
 * @param {boolean} withAuth - Indicates if authentication is required.
 * @returns {object} - The constructed item object.
 */
const getItem = (path, label, key, icon, children, withAuth) => {
  if (children) {
    return { label, key, icon, children, path, withAuth };
  }
  return { label, key, icon, path, withAuth };
};

/**
 * Retrieves header data from the provided array.
 * @param {Array} arr - The array containing header data.
 * @returns {Array} - The array of header data items.
 */
export const getHeaderData = (arr) => {
  if (arr instanceof Array) {
    return arr.reduce((prev, item) => {
      if (item?.belongsToHeader) {
        if (item.children instanceof Array) {
          const children = item.children.reduce(
            (prevElement, currentSubChild) => {
              if (currentSubChild?.belongsToHeader) {
                prevElement.push(
                  getItem(
                    currentSubChild?.path,
                    currentSubChild?.name,
                    currentSubChild?.key,
                    currentSubChild?.icon,
                    "",
                    currentSubChild?.withAuth
                  )
                );
              }
              return prevElement;
            },
            []
          );
          prev.push(
            getItem(
              item?.path,
              item?.name,
              item?.key,
              item?.icon,
              children,
              item?.withAuth
            )
          );
        } else {
          prev.push(
            getItem(
              item?.path,
              item?.name,
              item?.key,
              item?.icon,
              "",
              item?.withAuth
            )
          );
        }
      }
      return prev;
    }, []);
  }
  return [];
};

/**
 * Retrieves footer links from the provided array.
 * @param {Array} arr - The array containing footer link data.
 * @returns {Array} - The array of footer link items.
 */
export const getFooterLink = (arr) => {
  if (arr instanceof Array) {
    return arr.reduce((prev, item) => {
      if (item?.belongsToFooter) {
        if (item.children instanceof Array) {
          const children = item.children.reduce(
            (prevElement, currentSubChild) => {
              if (currentSubChild?.belongsToFooter) {
                prevElement.push(
                  getItem(
                    currentSubChild?.path,
                    currentSubChild?.name,
                    currentSubChild?.key,
                    currentSubChild?.icon,
                    "",
                    item?.withAuth
                  )
                );
              }
              return prevElement;
            },
            []
          );
          prev.push(
            getItem(
              item?.path,
              item?.name,
              item?.key,
              item?.icon,
              children,
              item?.withAuth
            )
          );
        } else {
          prev.push(
            getItem(
              item?.path,
              item?.name,
              item?.key,
              item?.icon,
              "",
              item?.withAuth
            )
          );
        }
      }
      return prev;
    }, []);
  }
  return [];
};

/**
 * Removes the token from the session storage.
 */
export const removeSessionStorageToken = () => {
  if (sessionStorage.getItem(`${config.NAME_KEY}:token`)) {
    sessionStorage.setItem(`${config.NAME_KEY}:token`, null);
  }
};

/**
 * Sets the token in the session storage after encrypting it.
 * @param {string} token - The token to be stored in session storage.
 */
export const setSessionStorageToken = (token) => {
  sessionStorage.setItem(
    `${config.NAME_KEY}:token`,
    CryptoJS.AES.encrypt(token, `${config.NAME_KEY}-token`).toString()
  );
};

/**
 * Removes the token from the local storage and navigates to a specific path if provided.
 * @param {Function} navigate - The navigate function from React Router.
 */
export const removeLocalStorageToken = (navigate) => {
  if (localStorage.getItem(`${config.NAME_KEY}:token`)) {
    localStorage.setItem(`${config.NAME_KEY}:token`, null);
  }
  if (navigate) {
    navigate("/backend-hf8a2w-91zj1b");
  }
};

/**
 * Retrieves the token from the session storage and decrypts it.
 * @returns {string|boolean} - The decrypted token or false if not found.
 */
export const getSessionStorageToken = () => {
  const ciphertext = sessionStorage.getItem(`${config.NAME_KEY}:token`);
  if (ciphertext) {
    const bytes = CryptoJS.AES.decrypt(ciphertext, `${config.NAME_KEY}-token`);
    return bytes.toString(CryptoJS.enc.Utf8);
  }
  return false;
};

/**
 * Sets the token in the local storage after encrypting it.
 * @param {string} token - Token to be stored in local storage.
 */
export const setLocalStorageToken = (token) => {
  localStorage.setItem(
    `${config.NAME_KEY}:token`,
    CryptoJS.AES.encrypt(token, `${config.NAME_KEY}-token`).toString()
  );
};

/**
 * Retrieves and decrypts the token from local storage.
 * @returns {string|boolean} - Decrypted token or false if not found.
 */
export const getLocalStorageToken = () => {
  const token = localStorage.getItem(`${config.NAME_KEY}:token`);
  if (token) {
    const bytes = CryptoJS.AES.decrypt(token, `${config.NAME_KEY}-token`);
    return bytes.toString(CryptoJS.enc.Utf8);
  }
  return false;
};

/**
 * Retrieves the language preference from local storage.
 * @returns {string} - Language preference or default language if not found.
 */
export const getLocalStorageLanguage = () => {
  const language = localStorage.getItem(`${config.NAME_KEY}:language`);
  if (language) {
    return ["en", "hi"].includes(language) ? language : config.DEFAULT_LANGUAGE;
  }
  return config.DEFAULT_LANGUAGE;
};

/**
 * Returns the complete URL.
 * @param {string} url - The URL to return.
 * @returns {string} - The complete URL.
 */
export const getCompleteUrl = (url) => {
  return url;
};

/**
 * Decodes the query string and returns an object.
 * @param {string} data - The query string to decode.
 * @returns {object} - Decoded query parameters as an object.
 */
export function decodeQueryData(data) {
  return JSON.parse(
    `{"${decodeURI(data)
      .replace(/"/g, '\\"')
      .replace(/&/g, '","')
      .replace(/=/g, '":"')
      .replace("?", "")}"}`
  );
}

/**
 * Navigates to a new URL path with query parameters.
 * @param {object} data - Object containing query parameters.
 * @param {function} navigate - Function to navigate to a new path.
 * @param {string} pathname - Pathname to navigate to.
 */
export const navigateWithParam = (data, navigate, pathname) => {
  const searchParams = new URLSearchParams(data).toString();
  navigate(`${pathname}?${searchParams}`);
};

/**
 * Returns the sort type based on the provided data.
 * @param {string} data - Sort order ("ASC" for ascending, "DESC" for descending).
 * @returns {string} - Sort type ("asc" for ascending, "desc" for descending).
 */
export function getSortType(data) {
  return data === "ASC" ? "asc" : "desc";
}

/**
 * Formats a date/time string into a specified format using moment.js library.
 * @param {string} params - The date/time string to be formatted.
 * @param {string} format - The format string to which the `params` should be formatted.
 *                           If not provided, it defaults to `config.DATE_FORMAT`.
 * @returns {string} - The formatted date/time string.
 */

export function dateFormatter(params, format) {
  return params ? moment(params).format(format ?? config.DATE_FORMAT) : "-";
}

/**
 * Formats a string by converting it to lowercase, replacing underscores with spaces,
 * and capitalizing the first letter of each word.
 * @param {string} str - The input string to be formatted.
 * @returns {string} - The formatted string.
 */
export function formatString(str) {
  return str
    ?.toLowerCase() // Convert the entire string to lowercase
    ?.replace(/_/g, " ") // Replace underscores with spaces
    ?.replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}


/**
 * Formats a date/time string into a specified format using dayjs library.
 * @param {string} params - The date/time string to be formatted.
 * @param {string} format - The format string to which the `params` should be formatted.
 *                           If not provided, it defaults to `config.DATE_FORMAT`.
 * @returns {string} - The formatted date/time string.
 */

export function dateFormatterDayJs(params, format) {
  return params ? dayjs(params).format(format ?? config.DATE_FORMAT) : "";
}
// .tz(moment.tz.guess())
export function dayJsDateFormatter(params, format) {
  return params
    ? dayjs(params)
        .tz(dayjs.tz.guess())
        .format(format ?? config.DATE_FORMAT)
    : "";
}

/**
 * Formats a date/time string from one format to another using moment.js library.
 * @param {string} param - The date/time string to be formatted.
 * @param {string} oldFormat - The current format of the `param` string.
 * @param {string} newFormat - The format string to which the `param` should be formatted.
 * @returns {string} - The formatted date/time string in the `newFormat`.
 */
export function dateFormatterWithFormat(param, oldFormat, newFormat) {
  return moment(param, oldFormat).format(newFormat);
}

// Use moment.js to parse the `param` string and format it according to the provided `format`
export function filterDateFormatter(param, format) {
  return moment(param).format(format);
}

/**
 * Encodes a string using Base64 encoding.
 * @param {string} code - The string to be encoded.
 * @returns {string} - The Base64 encoded string.
 */

export function encoder(code) {
  return window.btoa(code);
}

/**
 * Decodes a Base64 encoded string.
 * @param {string} str - The Base64 encoded string to be decoded.
 * @returns {string|false} - The decoded string, or false if the input is empty or invalid.
 */
export function decoder(str) {
  // return window.atob(code);
  if (str === "" || str.trim() === "") {
    return false;
  }
  try {
    return window.atob(str);
  } catch (err) {
    modalNotification({
      type: "error",
      message: "No treatment available",
    });
    return false;
  }
}

// Use Moment.js to format the date using the provided format string
export function momentDateFormatter(param, format) {
  return moment(param, format);
}

/**
 * Formats a time using Moment.js library.
 * @param {string|Date} param - The time string or Date object to be formatted.
 * @returns {object} - Moment.js object representing the formatted time.
 */
export const momentTimeFormatter = (param) => {
  // Use Moment.js to create a moment object representing the provided time
  return moment(param);
};

/**
 * Converts hours to minutes.
 * @param {number} hours - The number of hours to convert.
 * @param {number} [min=0] - The number of additional minutes to add (default is 0).
 * @returns {number} - Total minutes calculated from the given hours and additional minutes.
 */
export const convertToMinutes = (hours, min = 0) => {
  let minutes = hours * 60;

  return minutes + min;
};

/**
 * Generates an array of time values between a start time and an end time with a specified interval.
 * @param {string} startTime - The start time in "HH:mm" format.
 * @param {string} endTime - The end time in "HH:mm" format.
 * @param {number} minute - The interval in minutes between each time value.
 * @returns {Array} - An array of time values between the start and end times.
 */
export const getTime = (startTime, endTime, minute) => {
  let arr = [];
  for (
    let time = momentTimeFormatter(startTime);
    time <= momentTimeFormatter(endTime);
    time
      // .add(minute === 0 ? convertToMinutes(hour) : minute, "minutes")
      .add(minute, "minutes")
      .format("HH:mm")
  ) {
    arr.push(dateFormatter(time, "HH:mm"));
  }
  return arr;
};

/**
 * Generates shift timings based on provided data and shift parameters.
 * @param {Array} data - Array of time data.
 * @param {number} hours - Number of hours to add to each shift.
 * @param {number} minutes - Number of minutes to add to each shift.
 * @param {string} date - Date string in "YYYY-MM-DD" format.
 * @param {boolean} withRange - Flag indicating whether to include time ranges for shifts.
 * @returns {Object} - Object containing morning, afternoon, and night shifts.
 */

export const getTimeByShift = (
  data,
  hours,
  minutes,
  date,
  withRange = false
) => {
  let morning = [];
  let afternoon = [];
  let night = [];
  let shiftobj = {};
  if (withRange) {
    data.forEach((item) => {
      let nextTime = momentTimeFormatter(`${date} ${item}`)
        .add(
          // convertToMinutes(duration, minute) - (duration ? minute : 0),
          convertToMinutes(hours, minutes),
          "minutes"
        )
        .format("HH:mm");
      if (item <= "11:55") {
        morning.push(`${item} - ${nextTime}`);
      } else if (item <= "16:55") {
        afternoon.push(`${item} - ${nextTime}`);
      } else {
        night.push(`${item} - ${nextTime}`);
      }
    });
  } else {
    data.forEach((item) => {
      if (item <= "11:55") {
        morning.push(item);
      } else if (item <= "16:55") {
        afternoon.push(item);
      } else {
        night.push(item);
      }
    });
  }

  shiftobj = {
    morning,
    afternoon,
    night,
  };
  return shiftobj;
};

/**
 * Generates disabled time slots based on provided data, date, duration, and minute parameters.
 * @param {Object} data - Object containing time slots data.
 * @param {string} date - Date string in "YYYY-MM-DD" format.
 * @param {number} duration - Duration of the time slot in hours.
 * @param {number} minute - Number of minutes to subtract from the duration (default: 0).
 * @returns {Array} - Array containing disabled time slots.
 */

export const getDisabledTimeSlot = (data, date, duration, minute = 0) => {
  let arr = [];
  if (data?.[date]?.length > 0) {
    data?.[date].forEach((item) => {
      let nextTime = momentTimeFormatter(`${date} ${item}`)
        .add(
          // convertToMinutes(duration, minute) - (duration ? minute : 0),
          convertToMinutes(duration, minute) - 5,
          "minutes"
        )
        .format("DD-MM-YYYY HH:mm");
      let previousTime = momentTimeFormatter(`${date} ${item}`)
        .subtract(
          // convertToMinutes(duration, minute) - (duration ? minute : 0),
          convertToMinutes(duration, minute) - 5,
          "minutes"
        )
        .format("DD-MM-YYYY HH:mm");
      for (
        let time = momentDateFormatter(`${previousTime}`, "DD-MM-YYYY HH:mm");
        time <= momentDateFormatter(`${nextTime}`, "DD-MM-YYYY HH:mm");
        time.add(5, "minutes").format("HH:mm")
      ) {
        if (data?.[date].includes(dateFormatter(time, "HH:mm")) === false) {
          arr.push(dateFormatter(time, "HH:mm"));
        }
      }
    });
  }

  let unique = [...new Set(arr)];
  return unique;
};

/**
 * Capitalizes the first letter of a given text.
 * @param {string} text - The input text to capitalize.
 * @returns {string} - The text with the first letter capitalized.
 */
export const firstLetterCaps = (text) => {
  try {
    if (text) {
      const originalString = text.toLowerCase().split("");
      originalString[0] = originalString[0].toUpperCase();
      return originalString.join("");
    } else {
      return text;
    }
  } catch (error) {
    logger(error);
  }
};

/**
 * Displays text with an option to show more if the text exceeds a certain length.
 * @param {string} data - The text to display.
 * @param {function} showMoreText - Callback function to handle showing more text.
 * @param {string} extraReadClass - Additional CSS classes for the "Read More" link.
 * @returns {JSX.Element} - JSX element displaying the text with an option to show more.
 */
export function readMoreTextShow(data, showMoreText, extraReadClass) {
  if ([undefined, null, false].includes(data)) {
    return <>-</>;
  }
  if (data?.length < 25) {
    return <>{firstLetterCaps(data)}</>;
  }

  return (
    <p className="mb-0">
      {data.substring(0,25)}...
      {showMoreText ? (
        <Link
          to="/"
          onClick={(e) => {
            e.preventDefault();
            showMoreText({ data });
          }}
          className={`${extraReadClass}`}
        >
          <strong>Read More</strong>
          {/* {t("common.readMore")} */}
        </Link>
      ) : (
        ""
      )}
    </p>
  );
}

/**
 * Renders a formatted phone number with country code and contact number.
 * @param {string} countryCode - The country code of the phone number.
 * @param {string} contactNumber - The contact number excluding the country code.
 * @returns {JSX.Element} - JSX element displaying the formatted phone number.
 */
export function PhoneNumber({ countryCode, contactNumber }) {
  if (countryCode && contactNumber) {
    return <>{`${countryCode}-${contactNumber}`}</>;
  }
  return <span className="center">-</span>;
}

/**
 * Generates a regular expression pattern for validating OTP (One-Time Password) input.
 * @returns {RegExp} - Regular expression pattern for OTP validation.
 */
export function otpRegex() {
  let regex = /^[0-9]+$/;
  return regex;
}

/**
 * Generates a regular expression pattern for validating phone numbers.
 * @returns {RegExp} - Regular expression pattern for phone number validation.
 */

export function phoneRegex() {
  let regex =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
  return regex;
}

/**
 * Constructs sidebar data based on the provided array.
 * @param {Array} arr - Array containing sidebar data.
 * @returns {Array} - Constructed sidebar data.
 */
export const getSideBarData = (arr) => {
  if (arr instanceof Array) {
    return arr.reduce((prev, item) => {
      if (item?.belongsToSidebar) {
        if (item.children instanceof Array) {
          const children = item.children.reduce(
            (prevElement, currentSubChild) => {
              if (currentSubChild?.belongsToSidebar) {
                prevElement.push(
                  getItem(
                    currentSubChild?.path,
                    currentSubChild?.name,
                    currentSubChild?.key,
                    currentSubChild?.icon,
                    ""
                  )
                );
              }
              return prevElement;
            },
            []
          );
          prev.push(
            getItem(item?.path, item?.name, item?.key, item?.icon, children)
          );
        } else {
          prev.push(getItem(item?.path, item?.name, item?.key, item?.icon));
        }
      }
      return prev;
    }, []);
  }
  return [];
};

/**
 * Handles file preview functionality.
 * @param {Object} param0 - Object containing files.
 * @returns {string|null} - URL for file preview or null if no file is provided.
 */
export function handlePreview({ files }) {
  const [file] = files;
  if (file) {
    return URL.createObjectURL(file);
  }
}

/**
 * Sets data in session storage with encryption.
 * @param {string} keyName - The name/key under which the data will be stored.
 * @param {Object} formData - The data to be stored.
 */
export const setSessionStorage = (keyName, formData) => {
  let stringData = JSON.stringify(formData);
  sessionStorage.setItem(
    `${config.NAME_KEY}:${keyName}`,
    CryptoJS.AES.encrypt(stringData, `${config.NAME_KEY}-${keyName}`).toString()
  );
};

/**
 * Retrieves and decrypts data from session storage.
 * @param {string} keyName - The name/key under which the data is stored.
 * @returns {Object|boolean} - The decrypted data if found, otherwise false.
 */
export const getSessionStorage = (keyName) => {
  const cipherText = sessionStorage.getItem(`${config.NAME_KEY}:${keyName}`);
  if (cipherText) {
    const bytes = CryptoJS.AES.decrypt(
      cipherText,
      `${config.NAME_KEY}-${keyName}`
    );
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }
  return false;
};

/**
 * Removes data from session storage for a specified key.
 * @param {string} keyName - The name/key of the data to be removed.
 */
export const removeSessionStorage = (keyName) => {
  if (sessionStorage.getItem(`${config.NAME_KEY}:${keyName}`)) {
    sessionStorage.setItem(`${config.NAME_KEY}:${keyName}`, "");
  }
};

/**
 * Calculates the number of characters left based on the maximum allowed characters.
 * @param {string} char - The current characters.
 * @param {number} maxChar - The maximum allowed characters.
 * @returns {number} - The number of characters left.
 */
export const getCharLeft = (char, maxChar) => {
  if (char && maxChar) return maxChar - char.length;
  else return maxChar;
};

/**
 * Clears all data stored in session storage.
 */
export const clearSessionStorage = () => {
  sessionStorage.clear();
};

/**
 * Generates time slots based on the provided time slot data and duration.
 * @param {Object} timeSlotData - The time slot data grouped by date.
 * @param {number} durationHour - The duration hour for each time slot.
 * @param {number} durationMinute - The duration minute for each time slot.
 * @returns {Array} - An array of time slots grouped by date.
 */
export const generateTimeSlot = (
  timeSlotData,
  durationHour = 2,
  durationMinute = 30
) => {
  let dateSlot = [];
  Object.keys(timeSlotData).map((date) => {
    let timeSlot = [];
    timeSlotData[date].map((item) => {
      let toTime = momentTimeFormatter(`${date} ${item}`)
        .add(convertToMinutes(durationHour, durationMinute), "minutes")
        .format("HH:mm");
      timeSlot.push({
        from_time: item,
        to_time: toTime,
      });
    });
    dateSlot.push({
      date,
      slots: timeSlot,
    });
  });
  return dateSlot;
};

/**
 * Generates time slots data in edit mode based on the provided data.
 * @param {Array} data - The array of time slots data.
 * @returns {Object} - An object containing time slots data grouped by date.
 */
export const generateEditTimeSlot = (data) => {
  let dateData = {};
  data.map((e) => {
    dateData = { ...dateData, [e.date]: e.slots.map((z) => z.from_time) };
  });
  return dateData;
};

/**
 * Generates available time slots for editing based on the provided data and selected date.
 * @param {Array} data - The array of time slots data.
 * @param {string} selectDate - The selected date for editing.
 * @returns {Array} - An array of available time slots for editing.
 */
export const generateEditAvailableTimeSlot = (data, selectDate) => {
  let availableSlot = [];
  data.forEach((e) => {
    if (e.date === selectDate) {
      e.slots.forEach((z) => {
        if (z.availablity !== 1) {
          availableSlot.push(z.from_time);
        }
      });
    }
  });
  return availableSlot;
};

/**
 * Restricts input in a field to accept only phone number characters.
 * @param {Event} e - The keypress event object.
 */
export const phoneNumberField = (e) => {
  let ASCIICode = e.which ? e.which : e.keyCode;
  if (ASCIICode > 31 && (ASCIICode < 48 || ASCIICode > 57)) {
    e.preventDefault();
  }
};

/**
 * Initiates the download of a PDF file from the provided URL.
 * @param {string} url - The URL of the PDF file to download.
 */

export const downloadPdf = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = "Invoice.pdf";
  link.target = "_blank";
  link.click();
};

/**
 * Calculates the percentage of a value.
 * @param {number} value - The value for which the percentage needs to be calculated.
 * @param {number} percentage - The percentage to calculate.
 * @returns {number} - The calculated percentage of the value.
 */
export const getPercentage = (value, percentage) => {
  return (value / 100) * percentage;
};

/**
 * Generates the URL for adding an event to Google Calendar.
 * @param {string} bookingTitle - The title of the booking/event.
 * @param {Date} bookingDate - The date of the booking/event.
 * @param {string} toTime - The end time of the booking/event.
 * @returns {string} - The Google Calendar URL for adding the event.
 */

export const getAddGoogleCalenderUrl = (bookingTitle, bookingDate, toTime) => {
  let timeDate = `${dateFormatter(
    bookingDate,
    "YYYYMMDDTHHmmss"
  )}Z/${dateFormatter(bookingDate, "YYYYMMDDT")}${toTime?.replace(/:/g, "")}`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${bookingTitle}&dates=${timeDate}`;
};

/**
 * Computes the date and time that occurred ago.
 * @param {number} addHour - The number of hours to add/subtract.
 * @param {string} addType - The type of time unit to add/subtract (e.g., 'hours', 'days').
 * @param {string} format - The format of the resulting date and time.
 * @param {Date} date - The base date and time to compute from. Defaults to the current date and time if not provided.
 * @returns {string} - The formatted date and time.
 */
export const agoDateTime = (addHour, addType, format, date = "") => {
  let getCurrentTime = momentTimeFormatter(date || new Date());
  return getCurrentTime.add(addHour, addType).format(format);
};

/**
 * Converts an array of data into a string format.
 * @param {Array} data - The array of data to be converted.
 * @returns {string} - The converted string format of the data.
 */
export const arrayDataToString = (data) => {
  let uniqueData = [...new Set(data)];
  let newData = "";
  if (uniqueData) {
    uniqueData?.map((item, key) => {
      return uniqueData?.length !== key + 1
        ? (newData += `${item}, `)
        : (newData += `${item}.`);
    });
  }
  return data ? newData : "-";
};

/**
 * Determines the experience count from the provided data.
 * @param {Array} data - The array of experience data.
 * @returns {string|undefined} - The extracted experience count or undefined if not found.
 */

export const experienceCount = (data) => {
  if (data !== undefined) {
    let checkData = data.find((d) => {
      return d.search(/[+]/gi) > 0;
    });
    // console.log(data,checkData)
    // if (data.length === 1) {
    //   let exp = "";

    //   return (exp = data[0]);
    // } else
    if (checkData) {
      return checkData;
    }
    // else {
    //   data.map((item,key)=>{

    //     let text = item.replace(/[&\/\\#,()$~%'":*?<>{}!]/gi, '')
    //     // console.log(text,text.match(/\d{1,} year/gi))
    //     if(text.search(/[+]/gi) > 0){
    //       exp = text
    //     }else if(text.search(/[month]/gi) === -1){
    //       console.log(data)
    //      exp = text
    //     }
    //   })
    //   return exp

    // }
  }
  // return text
};

/**
 * Formats the given date into the specified format.
 * @param {Date} date - The date to be formatted.
 * @param {string} format - The format string for the date.
 * @returns {string} - The formatted date string.
 */
export const dateTimeFormatter = (date, format) => {
  return date ? moment(date).local().format(format) : "-";
};

/**
 * Shows the date in the browser with timezone offset.
 * @param {Date} data - The date to be displayed.
 * @returns {string} - The formatted date string with timezone offset.
 */

export const showDateInBrowser = (data) => {
  try {
    return moment(data)
      .add(moment.duration(5.3, "hours"))
      .format("ddd, MMM D, YYYY hh:mm A");
  } catch (error) {
    return moment(data).format("DD/MM/YYYY hh:mm A");
  }
};

/**
 * Checks if the provided data is valid and returns it or a fallback value.
 * @param {*} data - The data to be checked.
 * @returns {*} - The valid data or a fallback value.
 */
export const checkValidData = (data) => {
  return data || "-";
};

/**
 * Checks if the provided count data is valid and returns it or 0 as fallback.
 * @param {number} data - The count data to be checked.
 * @returns {JSX.Element} - The valid count or 0 as JSX element.
 */
export const checkValidCount = (data) => {
  return <>{data || 0}</>;
};

/**
 * Checks if the provided price data is valid and returns it or 0.00 as fallback.
 * @param {number} data - The price data to be checked.
 * @returns {JSX.Element} - The valid price or 0.00 as JSX element.
 */
export const checkValidPrice = (data) => {
  return (
    <>
      <sup>$</sup> {parseFloat(data || 0).toFixed(2)}
    </>
  );
};

/**
 * Renders an image element with optional preview source.
 * @param {Object} props - The component props.
 * @param {string} [props.previewSource=""] - The preview image source.
 * @param {string} props.source - The image source.
 * @param {string} [props.alt="image"] - The alternative text for the image.
 * @returns {JSX.Element} - The rendered image element.
 */
export function ImageElement({
  previewSource = "",
  source,
  alt = "image",
  ...rest
}) {
  let pattern = /^\//g;
  return (
    <>
      {previewSource ? (
        <img src={previewSource} alt={alt} {...rest} origin="anonymous" />
      ) : (
        <img
          src={`${config.IMAGE_URL}/${source?.replace(pattern, "")}`}
          alt={alt}
          {...rest}
        />
      )}
    </>
  );
}

/**
 * Formats an array of data into a string with commas.
 * @param {Array} data - The array of data to be formatted.
 * @returns {string} - The formatted string with commas.
 */
export function commasFormatter(data) {
  return data.join(", ");
}

/**
 * Renders an action dropdown component.
 * @param {Object} options - The dropdown options.
 * @param {string} extraClassName - The extra class name for styling.
 * @param {string} iconClass - The class name for the dropdown icon.
 * @param {string} toggleClass - The class name for the dropdown toggle.
 * @param {string} menuClass - The class name for the dropdown menu.
 * @param {string} itemClass - The class name for the dropdown items.
 * @param {string} span - The span content for the dropdown.
 * @returns {JSX.Element} - The rendered action dropdown component.
 */
export const actionFormatter = (
  options,
  extraClassName,
  iconClass,
  toggleClass,
  menuClass,
  itemClass,
  span
) => {
  return (
    <>
      <ActionDropdown
        options={options}
        extraClassName={extraClassName}
        iconClass={iconClass}
        toggleClass={toggleClass}
        menuClass={menuClass}
        itemClass={itemClass}
        span={span}
      />
    </>
  );
};

/**
 * Formats the serial number based on the page and limit.
 * @param {Object} param0 - The parameters.
 * @param {number} param0.page - The current page number.
 * @param {number} param0.limit - The limit of items per page.
 * @param {number} param0.index - The index of the item.
 * @returns {number} - The formatted serial number.
 */
export const serialNumberFormatter = ({ page, limit, index }) => {
  return (page - 1) * limit + index + 1;
};

/**
 * Renders a link element with optional link and extra class name.
 * @param {string} name - The name or text content of the link.
 * @param {string} [link="#"] - The link URL.
 * @param {string} [extraClassName=""] - The extra class name for styling.
 * @returns {JSX.Element} - The rendered link element.
 */
export const linkFormatter = (name, link = "#", extraClassName = "") => {
  return (
    <Link className={`${extraClassName}`} to={link}>
      {name}
    </Link>
  );
};

/**
 * Formats the name by concatenating first name and last name.
 * @param {string} firstName - The first name.
 * @param {string} lastName - The last name.
 * @returns {JSX.Element} - The formatted name element.
 */

export const nameFormatter = (firstName, lastName) => {
  return <>{firstName ? ` ${firstName} ${" "} ${lastName}` : "-"}</>;
};

/**
 * Formats the text by capitalizing the first letter.
 * @param {string} data - The input text data.
 * @returns {string} - The formatted text.
 */
export const textFormatter = (data) => {
  return data && data?.charAt(0)?.toUpperCase() + data.slice(1);
};

/**
 * Renders a dropdown component with the provided items.
 * @param {Array} items - The items to be displayed in the dropdown.
 * @returns {JSX.Element} - The rendered dropdown component.
 */
export function DropdownFormatter(items) {
  return (
    <>
      <AntDropdown
        sideBarData={items}
        placement="bottomRight"
        icon={<MoreOutlined />}
      />
    </>
  );
}

// export function readMoreTextShow(data, showMoreText) {
//   if ([undefined, null, false].includes(data)) {
//     return <></>;
//   }
//   if (data.props) {
//     if (data.props.children.length < 50) {
//       return <>{data}</>;
//     }
//   } else if (data.length < 50) {
//     return <>{data}</>;
//   }
//   return (
//     <p className="mb-0">
//       {data.props
//         ? data.props.children.type === "br"
//           ? " - "
//           : data.props.children.substring(0, 50)
//         : data.substring(0, 50)}

//       {data.props ? (data.props.children.type === "br" ? " " : "... ") : "..."}

//       {showMoreText ? (
//         <Link
//           to="/"
//           onClick={(e) => {
//             e.preventDefault();
//             showMoreText({ data });
//           }}
//         >
//           Read More
//           {/* {t("common.readMore")} */}
//         </Link>
//       ) : (
//         ""
//       )}
//     </p>
//   );
// }

/**
 * Retrieves an array of month names.
 * @returns {string[]} - Array of month names.
 */
export const getMonths = () => {
  return dayjs.months();
};

/**
 * Retrieves an array of days of the specified month and year.
 * @param {number} month - The month (1-indexed).
 * @param {number} year - The year.
 * @returns {number[]} - Array of days in the month.
 */
export const getDateOfMonth = (month, year) => {
  return Array.from(
    { length: dayjs(`${year}-${month}-01`).daysInMonth() },
    (_, i) => i + 1
  );
};

/**
 * Disables future dates for the start date field based on provided conditions.
 * @param {moment.Moment} current - The current date.
 * @param {Object} values - The form field values.
 * @param {string} dateFormat - The date format.
 * @param {Object} initialValues - The initial form values.
 * @returns {boolean} - Indicates if the date should be disabled.
 */
export const disableStartDateFutureDays = (
  current,
  values,
  dateFormat,
  initialValues
) => {
  let date;
  const end = momentDateFormatter(values.endDate, dateFormat);
  if (values.endDate === "") {
    date = !current.isBefore(moment());
  } else if (initialValues?.startDate || values.startDate === "") {
    date = !(current.isBefore(moment()) && end.isAfter(current));
  } else {
    date = !(current.isBefore(moment()) && end.add(1, "day").isAfter(current));
  }

  return date;
};

/**
 * Disables future dates for the end date field based on provided conditions.
 * @param {moment.Moment} current - The current date.
 * @param {string} dateFormat - The date format.
 * @param {string} fromDate - The start date from which the future dates are disabled.
 * @returns {boolean} - Indicates if the date should be disabled.
 */
export const disableEndDateFutureDays = (current, dateFormat, fromDate) => {
  let date;
  let start;
  if (fromDate === "") {
    start = momentDateFormatter(new Date(), dateFormat);
    // date = !start.isSameOrBefore(current);
  } else {
    start = momentDateFormatter(fromDate, dateFormat);
  }
  date = !(current.isSameOrAfter(start) && current.isSameOrBefore(moment()));
  return date;
};

/**
 * Formats the provided date using Day.js with the specified format.
 * @param {string} params - The date string to format.
 * @param {string} format - The format to apply.
 * @returns {object} - Day.js object representing the formatted date.
 */
export function dayJsFormatFormatter(params, format) {
  return dayjs(params, format);
}

/**
 * Generates the full name from provided first, middle, and last names.
 * @param {string} first - The first name.
 * @param {string} middle - The middle name.
 * @param {string} last - The last name.
 * @returns {string} - The full name.
 */
export const getFullName = (first, middle, last) => {
  try {
    let name = firstLetterCaps(first);
    if (!name) {
      return "";
    }
    if (middle) {
      name += ` ${firstLetterCaps(middle)}`;
    }
    if (last) {
      name += ` ${firstLetterCaps(last)}`;
    }
    return name;
  } catch (error) {
    logger(error);
  }
};

/**
 * Formats the provided date using Day.js.
 * @param {string} params - The date string to format.
 * @returns {object} - Day.js object representing the formatted date.
 */
export function dayJsFormatter(params) {
  return dayjs(params);
}

/**
 * Converts the provided date to a "from now" representation.
 * @param {string} myDate - The date string to convert.
 * @returns {string} - The "from now" representation of the date.
 */
export function dateToFromNowDaily(myDate) {
  return moment(myDate).calendar(null, {
    lastWeek: "[Last] dddd",
    lastDay: "[Yesterday]",
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    nextWeek: "dddd",
  });
}

/**
 * Calculates the total time count from the provided date.
 * @param {string} date - The date string.
 * @returns {string} - The total time count from now.
 */
export const totalTimeCount = (date) => {
  const updatedAt = new Date(date);
  return moment(updatedAt).fromNow();
};

/**
 * Object mapping letters to color codes.
 */
export const colorObj = {
  a: "danger",
  b: "warning",
  c: "blue",
  d: "danger",
  e: "warning",
  f: "danger",
  g: "warning",
  h: "warning",
  i: "purple",
  j: "warning",
  k: "warning",
  l: "blue",
  m: "purple",
  n: "purple",
  o: "info",
  p: "purple",
  q: "info",
  r: "info",
  s: "blue",
  t: "danger",
  u: "info",
  v: "purple",
  w: "blue",
  x: "danger",
  y: "blue",
  z: "purple",
};

/**
 * Calculates the length of unique members in a chat room.
 * @param {Array} chatRoomMembers - Array of chat room members.
 * @returns {number} - Length of unique members.
 */
export const getRoomMembersLength = (chatRoomMembers) => {
  if (!chatRoomMembers?.length) return 0;
  return chatRoomMembers?.filter?.(
    (value, index, self) =>
      self?.findIndex?.((selfItem) => selfItem?.userId === value?.userId) ===
      index
  )?.length;
};

/**
 * Object mapping file extensions to file icons.
 */
export const fileIcon = {
  pdf: { file: "pdf.svg", type: "pdf" },
  txt: { file: "txt.svg", type: "txt" },
  doc: { file: "docs.svg", type: "doc" },
  docx: { file: "doc.svg", type: "docx" },
  pptx: { file: "docs.svg", type: "pptx" },
  ppt: { file: "docs.svg", type: "ppt" },
  mp3: { file: "mp3.svg", type: "mp3" },
  mp4: { file: "mp4.svg", type: "mp4" },
  svg: { file: "svg.svg", type: "svg" },
  xlsx: { file: "xlsx.svg", type: "xlsx" },
  zip: { file: "zip.svg", type: "zip" },
  jpg: { file: "jpg.svg", type: "jpg" },
  png: { file: "jpg.svg", type: "png" },
  file: { file: "file.svg", type: "file" },
};

/**
 * Calculates the day difference between two dates.
 * @param {Date} start - Start date.
 * @param {Date} end - End date.
 * @returns {number} - Day difference between the two dates.
 */
export const dayDiff = (start, end) => {
  let diff = end.getDate() - start.getDate();
  return diff;
};

/**
 * Formats the date to show according to the proximity to the current date.
 * @param {Date} date - The date to format.
 * @param {Date} endDate - The end date to compare.
 * @returns {string} - Formatted date string.
 */
export const showDate = (date, endDate) => {
  let dates = "";
  dates =
    dayDiff(new Date(date), endDate ? new Date(endDate) : new Date()) <= 7
      ? dateFormatter(date, "dddd HH:mm A")
      : dateFormatter(date, "DD-MM-YYYY HH:mm A");
  return date ? dates : "-";
};

/**
 * Formats the user status for display.
 * @param {string} status - The status of the user.
 * @param {string} type - The type of formatting (profileStatus or statusdot).
 * @returns {string|object} - Formatted status string or JSX element.
 */

export const statusFormatter = (status, type) => {
  const statusArr = {
    online: "statusdot statusdot-online",
    away: "statusdot statusdot-away",
    busy: "statusdot statusdot-busy",
    dnd: "statusdot statusdot-dnd",
    offline: "statusdot statusdot-offline",
    in_meeting: "statusdot statusdot-busy",
  };

  const profileStatusArr = {
    online: (
      <span className="text-success font-sb">
        {i18next.t("text.chat.online")}
      </span>
    ),
    away: (
      <span className="text-warning font-sb">
        {i18next.t("text.chat.away")}
      </span>
    ),
    busy: (
      <span className="text-danger font-sb">{i18next.t("text.chat.busy")}</span>
    ),
    dnd: (
      <span className="text-danger font-sb">{i18next.t("text.chat.dnd")}</span>
    ),
    offline: (
      <span className="text-light font-sb">
        {i18next.t("text.chat.offline")}
      </span>
    ),
    in_meeting: (
      <span className="text-danger font-sb">
        {i18next.t("text.chat.inMeeting")}
      </span>
    ),
  };
  return type === "profileStatus"
    ? profileStatusArr[status]
    : statusArr[status];
};

/**
 * Retrieves the file extension from the file name.
 * @param {string} fileName - The name of the file.
 * @returns {string} - The file extension.
 */
export const getFileExtension = (fileName) => {
  const lastDot = fileName.lastIndexOf(".");

  const fileExt = fileName.substring(lastDot + 1);

  return fileExt;
};

/**
 * Calculates the time difference between two timestamps.
 * @param {Date} createdAt - The creation timestamp.
 * @param {Date} updatedAt - The update timestamp.
 * @returns {string} - The formatted time difference.
 */
export const totalTimeDifference = (createdAt, updatedAt) => {
  let days = dayDiff(new Date(createdAt), new Date());
  let startTime = moment(createdAt);
  let endTime = moment(updatedAt || new Date());
  let difference = startTime.from(endTime);
  let time = "";
  if (days < 1) {
    time = difference;
  } else if (days === 1) {
    time = `Yesterday ${dateFormatter(createdAt, "HH:mm A")}`;
  } else if (days <= 7 && days !== 1) {
    time = dateFormatter(createdAt, "dddd HH:mm A");
  } else if (days > 7) {
    time = dateFormatter(createdAt, "DD-MMM-YYYY HH:mm A");
  }
  return time;
};

export const icons = {
  pdf: { file: "pdf.svg", type: "pdf" },
  txt: { file: "txt.svg", type: "txt" },
  doc: { file: "docs.svg", type: "doc" },
  docx: { file: "doc.svg", type: "docx" },
  pptx: { file: "docs.svg", type: "pptx" },
  ppt: { file: "docs.svg", type: "ppt" },
  mp3: { file: "mp3.svg", type: "mp3" },
  mp4: { file: "mp4.svg", type: "mp4" },
  xlsx: { file: "xlsx.svg", type: "xlsx" },
  zip: { file: "zip.svg", type: "zip" },
  file: { file: "file.svg", type: "file" },
  // jpg: { file: "jpg.svg", type: "jpg" },
};

/**
 * Retrieves user profile images, statuses, or other data for chat purposes.
 * @param {Object} fromUser - The user initiating the chat.
 * @param {Object} toUser - The user receiving the chat.
 * @param {string} type - The type of data to retrieve (image, status, dotStatus, email, company, number).
 * @param {Object} userData - The data of the current user.
 * @returns {string} - The requested user data.
 */
export const getChatData = (fromUser, toUser, type, userData) => {
  let data = "";
  if (type === "image") {
    if (fromUser?.id !== userData?.id) {
      data = fromUser?.profileImageUrl || fromUser?.user?.profileImageUrl;
      return data;
    } else if (fromUser?.chatRoomMembers) {
      if (fromUser?.chatRoomMembers[0]?.id === userData?.id) {
        return fromUser?.chatRoomMembers[1].userProfileImageUrl;
      } else {
        return fromUser?.chatRoomMembers[0].userProfileImageUrl;
      }
    } else {
      data = toUser?.profileImageUrl;
    }
  } else if (type === "status") {
    if (fromUser?.id !== userData?.id) {
      data = fromUser?.chatStatus?.status;
      return data;
    } else {
      data = toUser?.chatStatus?.status;
    }
  } else if (type === "dotStatus") {
    if (fromUser?.id !== userData?.id) {
      data = statusFormatter(fromUser?.chatStatus?.status);
      return data;
    } else {
      data = statusFormatter(toUser?.chatStatus?.status);
    }
  } else if (type === "email") {
    if (fromUser?.id !== userData?.id) {
      data = fromUser?.email;
      return data;
    } else {
      data = toUser?.email;
    }
  } else if (type === "comapny") {
    if (fromUser?.id !== userData?.id) {
      data = fromUser?.companyName;
      return data;
    } else {
      data = toUser?.companyName;
    }
  } else if (type === "number") {
    if (fromUser?.id !== userData?.id) {
      data = fromUser?.phoneNumber;
      return data;
    } else {
      data = toUser?.phoneNumber;
    }
  } else if (fromUser?.id !== userData?.id) {
    data = getFullName(fromUser?.firstName, fromUser?.lastName);
    return data;
  } else {
    data = getFullName(toUser?.firstName, toUser?.lastName);
  }
  return data;
};

/**
 * Options for user chat statuses.
 */
export const userChatStatusOptions = {
  online: {
    label: "Available",
    key: "online",
    id: 1,
  },
  busy: {
    label: "Busy",
    key: "busy",
    id: 3,
  },
  do_not_disturb: {
    label: "Do not disturb",
    key: "dnd",
    id: 4,
  },
  dnd: {
    label: "Do not disturb",
    key: "dnd",
    id: 4,
  },
  away: {
    label: "Appear away",
    key: "away",
    id: 2,
  },
  offline: {
    label: "Offline",
    key: "offline",
    id: 5,
  },
  in_meeting: {
    label: "In Meeting",
    key: "in_meeting",
    id: 6,
  },
};

/**
 * Retrieves data for pinned chats.
 * @param {Array} val - The array of pinned chats.
 * @param {string} type - The type of data to retrieve (image, name, status).
 * @param {Object} userData - The data of the current user.
 * @returns {string} - The requested pinned chat data.
 */
export const getChatPinData = (val, type, userData) => {
  let name = "";
  if (type === "image") {
    let data = val?.find((item) => item?.userId !== userData?.id);
    name = data?.user?.profileImageUrl;
    return name;
  } else if (type === "name") {
    let data = val?.find((item) => item?.userId !== userData?.id);
    name = getFullName(data?.user?.firstName, data?.user?.lastName);
    return name;
  } else if (type === "status") {
    let data = val?.find((item) => item?.userId !== userData?.id);
    name = data?.user?.chatStatus?.status;
    return name;
  }
  return name;
};

/**
 * Component to create a downloadable link for an image.
 * @param {string} url - URL of the image to download.
 * @param {string} name - Name of the downloaded image file.
 * @param {string} extraClassName - Additional CSS classes for styling.
 * @param {ReactNode} children - Child components or elements.
 */
export function DownloadImage({ url, name, extraClassName, children }) {
  const downloadImage = () => {
    saveAs(url, name);
  };
  return (
    <a
      href="#"
      className={`${extraClassName}`}
      onClick={(e) => {
        e.preventDefault();
        downloadImage();
      }}
    >
      {children}
    </a>
  );
}

/**
 * Returns a new array containing only unique elements based on the specified key.
 * @param {Array} arr - Original array to filter.
 * @param {string} key - Key to determine uniqueness.
 * @returns {Array} - New array with unique elements.
 */
export const getUniqueListByKey = (arr, key) => {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
};

/**
 * Generates a popover with a list of channel names for user selection.
 * @param {string} channel - Current channel name or identifier.
 * @param {Array} channelName - List of channel names or roles.
 * @param {Function} onHandleClick - Callback function for handling clicks on channel names.
 * @param {ReactNode} icon - Icon component to display alongside the channel name.
 * @returns {JSX.Element} - JSX element representing the user dropdown formatter.
 */
export const userDropdownFormatter = (
  channel,
  channelName,
  onHandleClick,
  icon
) => {
  return (
    <Popovers
      placement="bottom"
      overlayClassName="channelInfo"
      title=""
      content={
        <ul className="list-unstyled m-0">
          {channelName?.map((item) => {
            return (
              <li>
                <Link
                  className="dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    onHandleClick(item?.id);
                  }}
                  to="#"
                >
                  {item.name || item?.channelRole?.role}
                </Link>
              </li>
            );
          })}
        </ul>
      }
      trigger="hover"
    >
      <span className="cursor-pointer m-0 table-dropDown">
        {channel} {icon}
      </span>
    </Popovers>
  );
};

/**
 * Formats a given time value (in seconds) into a human-readable timer format (e.g., "mm:ss").
 * @param {number} time - Time value in seconds.
 * @returns {string} - Formatted timer string (e.g., "mm:ss").
 */
export const TimerFormat = (time) => {
  const minutes = Math.floor(time / 60);

  const seconds = time % 60;
  let secs = seconds > 9 ? seconds : `0${seconds}`;

  return `${minutes}:${secs}`;
};

/**
 * Determines the media type of an uploaded file based on its file extension.
 * @param {string} fileExt - File extension.
 * @returns {string} - Media type ("image", "video", "audio", "file", or "media").
 */
export const getUploadFileMediaType = (fileExt) => {
  let result = "file";
  if (["png", "jpg", "gif", "jpeg"].includes(fileExt)) {
    result = "image";
  } else if (["mp4", "mov", "wmv"].includes(fileExt)) {
    result = "video";
  } else if (["aac", "m4a", "mp3"].includes(fileExt)) {
    result = "audio";
  } else if (["pdf", "doc", "docx"].includes(fileExt)) {
    result = "file";
  } else if (
    [
      "png",
      "jpg",
      "gif",
      "aac",
      "m4a",
      "mp3",
      "jpeg",
      "pdf",
      "doc",
      "docx",
      "mp4",
      "mov",
      "wmv",
    ].includes(fileExt)
  ) {
    result = "media";
  }

  return result;
};

export function capitalStringFirstLetter(string) {
  return string?.replace(/\b\w/g, (match) => match.toUpperCase());
}